import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { extractLanguageFromPath, LanguageCode, SUPPORTED_LANGUAGES } from "../utils/languageRouting";
import { FALLBACK_COUNTRY_INITIAL, LANGUAGE_TO_COUNTRY_INITIAL } from "../constants/locationSeo";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { fetchLocationSeosByCountry } from "../redux/Slices/LocationSeoSlice";
import { LocationCountry, LocationSeo } from "../modules/shared/model";
import { getCountries as getCountriesAction } from "../redux/Actions/LocationActions";

type LocationSeoManagerProps = {
  children: React.ReactNode;
};

const DEFAULT_TITLE = "Kokobeo";
const DEFAULT_DESCRIPTION = "Kokobeo connects clients with trusted professionals around the world.";

const deriveLanguageCode = (rawLanguage: string): LanguageCode => {
  const normalized = (rawLanguage || "en").split("-")[0].toLowerCase();
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(normalized)) {
    return normalized as LanguageCode;
  }
  return "en";
};

const selectPreferredSeoRecord = (records: LocationSeo[]): LocationSeo | null => {
  if (!records || records.length === 0) {
    return null;
  }
  const withMetaTitle = records.find((record) => record.metaTitle);
  if (withMetaTitle) {
    return withMetaTitle;
  }
  const withMetaDescription = records.find((record) => record.metaDescription);
  if (withMetaDescription) {
    return withMetaDescription;
  }
  return records[0];
};

const buildOgLocale = (language: LanguageCode, countryInitial: string | null) => {
  const country = (countryInitial || LANGUAGE_TO_COUNTRY_INITIAL[language] || FALLBACK_COUNTRY_INITIAL).toUpperCase();
  return `${language}_${country}`;
};

const ensureAbsoluteUrl = (url: string | null | undefined, fallback: string): string => {
  if (!url) return fallback;
  if (/^https?:\/\//i.test(url)) return url;

  if (typeof window === "undefined") {
    return url || fallback;
  }

  if (url.startsWith("//")) return `${window.location.protocol}${url}`;
  if (url.startsWith("/")) return `${window.location.origin}${url}`;
  return url;
};

const LocationSeoManager: React.FC<LocationSeoManagerProps> = ({ children }) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { countries } = useAppSelector((state) => state.locations);
  const [currentSeo, setCurrentSeo] = useState<LocationSeo | null>(null);
  const [appliedCountryInitial, setAppliedCountryInitial] = useState<string | null>(null);
  const seoCacheRef = useRef<Record<string, LocationSeo | null>>({});
  const countryCacheRef = useRef<Record<string, LocationCountry | null>>({});
  const pendingFetchRef = useRef<Promise<LocationSeo | null> | null>(null);

  const { language: languageFromPath } = useMemo(() => extractLanguageFromPath(location.pathname), [location.pathname]);
  const activeLanguage: LanguageCode = useMemo(() => {
    if (languageFromPath) return languageFromPath;
    return deriveLanguageCode(i18n.language);
  }, [languageFromPath, i18n.language]);

  const targetCountryInitial = LANGUAGE_TO_COUNTRY_INITIAL[activeLanguage] || FALLBACK_COUNTRY_INITIAL;

  useEffect(() => {
    let isSubscribed = true;

    const getCountryByInitial = async (initial: string): Promise<LocationCountry | null> => {
      const key = initial.toUpperCase();

      if (countryCacheRef.current[key] !== undefined) {
        return countryCacheRef.current[key];
      }

      const existing = countries.find(
        (country: LocationCountry) => country.initial?.toUpperCase() === key
      );

      if (existing) {
        countryCacheRef.current[key] = existing;
        return existing;
      }

      try {
        const response = await getCountriesAction({ initial: key, limit: 1 });
        const country = response.data?.[0] || null;
        countryCacheRef.current[key] = country;
        return country;
      } catch (error) {
        console.error(`[LocationSeoManager] Failed to lookup country ${key}:`, error);
        countryCacheRef.current[key] = null;
        return null;
      }
    };

    const fetchSeoByInitial = async (countryInitial: string): Promise<LocationSeo | null> => {
      const key = countryInitial.toUpperCase();

      if (seoCacheRef.current[key] !== undefined) {
        return seoCacheRef.current[key];
      }

      const countryMatch = await getCountryByInitial(key);

      if (!countryMatch) {
        console.warn(`[LocationSeoManager] Country with initial "${key}" not found in cache.`);
        seoCacheRef.current[key] = null;
        return null;
      }

      try {
        const records = await dispatch(fetchLocationSeosByCountry(countryMatch.id)).unwrap();
        const seo = selectPreferredSeoRecord(records);
        seoCacheRef.current[key] = seo || null;
        return seo || null;
      } catch (error) {
        console.error(`[LocationSeoManager] Failed to load location SEO for ${key}:`, error);
        seoCacheRef.current[key] = null;
        return null;
      }
    };

    const resolveSeo = async () => {
      if (pendingFetchRef.current) {
        await pendingFetchRef.current;
      }

      const executeFetch = async (): Promise<LocationSeo | null> => {
        let seo = await fetchSeoByInitial(targetCountryInitial);
        let countryApplied = targetCountryInitial;

        if (!seo && targetCountryInitial !== FALLBACK_COUNTRY_INITIAL) {
          seo = await fetchSeoByInitial(FALLBACK_COUNTRY_INITIAL);
          countryApplied = seo ? FALLBACK_COUNTRY_INITIAL : countryApplied;
        }

        if (isSubscribed) {
          setCurrentSeo(seo);
          setAppliedCountryInitial(seo ? countryApplied : null);
        }

        return seo;
      };

      pendingFetchRef.current = executeFetch();
      await pendingFetchRef.current;
      pendingFetchRef.current = null;
    };

    resolveSeo();

    return () => {
      isSubscribed = false;
    };
  }, [countries, dispatch, targetCountryInitial]);

  const canonicalFallback = useMemo(() => {
    if (typeof window === "undefined") return "";
    const url = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    return url;
  }, [location.pathname, location.search, location.hash]);

  const seoMeta = currentSeo;
  const locale = buildOgLocale(activeLanguage, appliedCountryInitial);
  const htmlAttributes = { lang: activeLanguage, "data-country": (appliedCountryInitial || targetCountryInitial).toLowerCase() };
  const title = seoMeta?.metaTitle || DEFAULT_TITLE;
  const description = seoMeta?.metaDescription || DEFAULT_DESCRIPTION;
  const canonical = ensureAbsoluteUrl(seoMeta?.canonicalUrl, canonicalFallback);
  const keywords = seoMeta?.keywords?.join(", ");
  const ogTitle = seoMeta?.ogTitle || title;
  const ogDescription = seoMeta?.ogDescription || description;
  const ogImage = ensureAbsoluteUrl(seoMeta?.ogImageUrl, "");
  const hasOgImage = Boolean(seoMeta?.ogImageUrl);

  return (
    <>
      <Helmet htmlAttributes={htmlAttributes}>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={canonical} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:locale" content={locale} />
        <meta property="og:url" content={canonical} />
        {hasOgImage && <meta property="og:image" content={ogImage} />}

        <meta name="twitter:card" content={hasOgImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        {hasOgImage && <meta name="twitter:image" content={ogImage} />}
      </Helmet>
      {children}
    </>
  );
};

export default LocationSeoManager;


